/* Quantity-break buy box (issue #332).

   Owns the whole buy box for a `qty-tiers` product — price, tier table, quantity, the custom
   fields, and add-to-cart. The native price / variant picker / quantity / buy buttons are
   suppressed for this tag in snippets/product-page.liquid, the same shape as
   product-smoothie-boosts.liquid.

   ⚠️ WHY THIS POSTS ITS OWN FORMDATA INSTEAD OF USING ELLA'S <product-form>.
   Ella's serializeForm (assets/global.js:87) does `new FormData(form)` and then JSON.stringify's
   the result. A File stringifies to `{}`, so the AJAX path CANNOT carry an uploaded file — the
   artwork silently vanishes and the order arrives with nothing to print. Posting the FormData
   itself (no Content-Type header, so the browser sets the multipart boundary) works: verified
   against the live cart 2026-08-13, which stored the file and returned a cdn.shopify.com URL as
   the property value.

   ⚠️ The visible inputs deliberately carry NO `name="properties[...]"`. Ella harvests every
   `[name^="properties"]` element on the PAGE, not just inside its own form (docs/EVENT-PICKUP-FIELDS.md,
   issue #279). Property names live in data attributes and the body is assembled in submit().
*/
(function () {
  'use strict';

  function formatMoney(cents, format) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        return window.Shopify.formatMoney(cents, format);
      } catch (e) {
        /* fall through to the local formatter */
      }
    }
    var amount = (cents / 100).toFixed(2);
    var parts = amount.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    amount = parts.join('.');
    if (format && format.indexOf('{{') !== -1) {
      return format.replace(/\{\{\s*amount[^}]*\}\}/, amount);
    }
    return '$' + amount;
  }

  function init(root) {
    var cfgEl = root.querySelector('[data-qt-config]');
    if (!cfgEl) return;

    var cfg;
    try {
      cfg = JSON.parse(cfgEl.textContent);
    } catch (e) {
      return;
    }

    var tiers = cfg.tiers || [];
    if (!tiers.length) return;

    var qtyEl = root.querySelector('[data-qt-qty]');
    var unitEl = root.querySelector('[data-qt-unit]');
    var totalEl = root.querySelector('[data-qt-total]');
    var errEl = root.querySelector('[data-qt-error]');
    var btn = root.querySelector('[data-qt-submit]');
    var minusEl = root.querySelector('[data-qt-step="-1"]');
    var plusEl = root.querySelector('[data-qt-step="1"]');
    var rows = Array.prototype.slice.call(root.querySelectorAll('[data-qt-row]'));
    var fieldEls = Array.prototype.slice.call(root.querySelectorAll('[data-qt-prop]'));

    if (!qtyEl || !btn) return;

    var floorQty = tiers[0].min;
    var lastTier = tiers[tiers.length - 1];
    var ceilQty = lastTier.max || 0; // 0 means the top bracket is open-ended (`49+`)
    var current = tiers[0];

    function tierFor(q) {
      for (var i = 0; i < tiers.length; i++) {
        if (q >= tiers[i].min && (!tiers[i].max || q <= tiers[i].max)) return tiers[i];
      }
      return q < floorQty ? tiers[0] : lastTier;
    }

    function clamp(q) {
      if (isNaN(q) || q < floorQty) return floorQty;
      if (ceilQty && q > ceilQty) return ceilQty;
      return q;
    }

    function showError(msg) {
      if (!errEl) return;
      errEl.textContent = msg || '';
      errEl.hidden = !msg;
    }

    function recalc() {
      var q = clamp(parseInt(qtyEl.value, 10));
      if (String(q) !== qtyEl.value) qtyEl.value = String(q);

      current = tierFor(q);

      if (unitEl) unitEl.textContent = formatMoney(current.price, cfg.moneyFormat);
      if (totalEl) {
        totalEl.textContent = (cfg.totalLabel || 'Total %s').replace(
          '%s',
          formatMoney(current.price * q, cfg.moneyFormat)
        );
      }

      rows.forEach(function (row) {
        if (row.getAttribute('data-qt-row') === String(current.id)) {
          row.setAttribute('aria-current', 'true');
        } else {
          row.removeAttribute('aria-current');
        }
      });

      if (minusEl) minusEl.disabled = q <= floorQty;
      if (plusEl) plusEl.disabled = !!ceilQty && q >= ceilQty;
    }

    function fieldError(el, msg) {
      var wrap = el.closest('.qt__field');
      var node = wrap ? wrap.querySelector('[data-qt-field-error]') : null;
      if (!node) return;
      node.textContent = msg || node.getAttribute('data-qt-default-error') || 'Please fill this in.';
      node.hidden = !msg;
    }

    function validate() {
      var ok = true;
      var firstBad = null;

      fieldEls.forEach(function (el) {
        var required = el.hasAttribute('data-qt-required');
        var empty =
          el.type === 'file' ? !(el.files && el.files.length) : !String(el.value || '').trim();

        if (required && empty) {
          fieldError(el, el.getAttribute('data-qt-message') || 'Please fill this in.');
          if (!firstBad) firstBad = el;
          ok = false;
          return;
        }

        if (el.type === 'file' && el.files && el.files.length) {
          var max = parseInt(el.getAttribute('data-qt-maxbytes') || '0', 10);
          if (max && el.files[0].size > max) {
            fieldError(
              el,
              'That file is too large. Please keep it under ' + Math.floor(max / 1048576) + ' MB.'
            );
            if (!firstBad) firstBad = el;
            ok = false;
            return;
          }
        }

        fieldError(el, '');
      });

      if (firstBad && typeof firstBad.focus === 'function') {
        firstBad.focus();
        if (typeof firstBad.scrollIntoView === 'function') {
          firstBad.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      }

      return ok;
    }

    function submit() {
      showError('');
      if (!validate()) return;

      var qty = clamp(parseInt(qtyEl.value, 10));
      var body = new FormData();
      body.append('id', String(current.id));
      body.append('quantity', String(qty));

      fieldEls.forEach(function (el) {
        var key = 'properties[' + el.getAttribute('data-qt-prop') + ']';
        if (el.type === 'file') {
          if (el.files && el.files.length) body.append(key, el.files[0]);
        } else {
          var val = String(el.value || '').trim();
          // An empty optional field is omitted entirely. Appending "" would put a blank property
          // on the order, which is the #279 empty-mirror bug in a different costume.
          if (val) body.append(key, val);
        }
      });

      var original = btn.textContent;
      btn.disabled = true;
      btn.textContent = cfg.addingLabel || 'Adding…';

      // No Content-Type header on purpose — the browser must set the multipart boundary itself.
      fetch(cfg.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/javascript' },
        body: body
      })
        .then(function (res) {
          return res.json().then(function (payload) {
            return { ok: res.ok, payload: payload };
          });
        })
        .then(function (result) {
          if (!result.ok) {
            showError(
              (result.payload && (result.payload.description || result.payload.message)) ||
                'Could not add this to your cart. Please try again.'
            );
            btn.disabled = false;
            btn.textContent = original;
            return;
          }
          window.location.href = cfg.cartUrl || '/cart';
        })
        .catch(function () {
          showError('Network error. Please check your connection and try again.');
          btn.disabled = false;
          btn.textContent = original;
        });
    }

    qtyEl.addEventListener('input', recalc);
    qtyEl.addEventListener('change', recalc);
    qtyEl.addEventListener('blur', recalc);

    if (minusEl) {
      minusEl.addEventListener('click', function () {
        qtyEl.value = String(clamp(parseInt(qtyEl.value, 10) - 1));
        recalc();
      });
    }

    if (plusEl) {
      plusEl.addEventListener('click', function () {
        qtyEl.value = String(clamp(parseInt(qtyEl.value, 10) + 1));
        recalc();
      });
    }

    rows.forEach(function (row) {
      function pick() {
        qtyEl.value = row.getAttribute('data-qt-min');
        recalc();
        qtyEl.focus();
      }
      row.addEventListener('click', pick);
      row.addEventListener('keydown', function (evt) {
        if (evt.key === 'Enter' || evt.key === ' ') {
          evt.preventDefault();
          pick();
        }
      });
    });

    fieldEls.forEach(function (el) {
      if (el.hasAttribute('data-qt-digits')) {
        el.addEventListener('input', function () {
          el.value = el.value.replace(/\D/g, '');
        });
      }
      if (el.type === 'file') {
        el.addEventListener('change', function () {
          var name = el.closest('.qt__field').querySelector('[data-qt-filename]');
          if (name) {
            name.textContent = el.files && el.files.length ? el.files[0].name : '';
            name.hidden = !(el.files && el.files.length);
          }
          fieldError(el, '');
        });
      } else {
        el.addEventListener('input', function () {
          fieldError(el, '');
        });
      }
    });

    btn.addEventListener('click', submit);

    root.removeAttribute('data-qt-pending');
    recalc();
  }

  function boot() {
    document.querySelectorAll('[data-qt]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
